import { FormInstance } from 'antd';
import { Rule } from 'antd/lib/form';
import { Dictionary } from 'lodash';
import { union } from 'ramda';
import React, { FC, useMemo } from 'react';
import styled from 'styled-components';
import { Loading } from 'components/atoms/loading';
import { Option } from 'components/atoms/select';
import { Member } from 'data/members/api';
import { useGetAllNativeUsersFromIndexedDb } from 'hooks/use-idb';
import { useTranslation } from 'hooks/use-translation';
import { useAccountProfile } from 'queries/account';
import { byLabel } from 'utils/comparators/by-label';
import { fullName } from 'utils/full-name';
import { getRandomColor } from 'utils/get-random-color';
import {
  InviteMemberFormPayload,
  InviteModalFormV2,
} from '../invite-modal/form-v2';
import { TeamMemberDrawerSectionProps } from '../types';
import { TeamMemberViewModal } from '../view-modal/view-modal';

const LoaderWrapper = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const TIMESHEET_ADMIN_ID = -1;

export const TeamMemberDrawerBody: FC<{
  readOnly: boolean;
  form: FormInstance<InviteMemberFormPayload>;
  member?: Member;
  changeToEditMode(member: Member): void;
  sectionProps: TeamMemberDrawerSectionProps;
  permissionOptions: Option[];
  roleOptions: Option[];
  formInputRules?: Dictionary<Rule[]>;
  isVendorInvite: boolean;
}> = ({
  form,
  readOnly,
  member,
  changeToEditMode,
  sectionProps,
  permissionOptions,
  roleOptions,
  formInputRules,
  isVendorInvite,
}) => {
  const { data: account } = useAccountProfile();

  const adminCaption = useTranslation(
    'settings.timesheets.controls.approvals.admin',
  );

  const adminOption = useMemo(
    () => ({
      label: adminCaption,
      value: TIMESHEET_ADMIN_ID,
    }),
    [adminCaption],
  );

  const nativeUsers = useGetAllNativeUsersFromIndexedDb();

  const approverOptions = useMemo(
    () =>
      union(
        [adminOption],
        nativeUsers
          .map((user) => ({
            label: fullName(user),
            value: user.userId,
            imageUrl: user.photoUrl,
            description: user.role?.roleName,
            color: getRandomColor(user.userId),
          }))
          .sort(byLabel),
      ),
    [adminOption, nativeUsers],
  );

  return readOnly ? (
    member ? (
      <TeamMemberViewModal
        member={member}
        changeToEditMode={changeToEditMode}
        sectionProps={sectionProps}
      />
    ) : (
      <></>
    )
  ) : account != null ? (
    <InviteModalFormV2
      form={form}
      member={member}
      company={account.company}
      approverOptions={approverOptions}
      sectionProps={sectionProps}
      permissionOptions={permissionOptions}
      roleOptions={roleOptions}
      formInputRules={formInputRules}
      isVendorInvite={isVendorInvite}
    />
  ) : (
    <LoaderWrapper>
      <Loading size="large" />
    </LoaderWrapper>
  );
};
